import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { companyQuery } from "#modules/company/infra/company.query";
import { bulkDeleteOnboardingProcessStepsApi } from "#modules/onboardingV2/infra/bulkDeleteOnboardingProcessSteps.api";
import {
  getOnboardingProcessStepsQueryData,
  onboardingProcessStepsQuery,
  setOnboardingProcessStepsQueryData,
} from "#modules/onboardingV2/infra/onboardingProcessSteps.query";

export const useBulkDeleteOnboardingProcessSteps = () => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: bulkDeleteOnboardingProcessStepsApi,
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: onboardingProcessStepsQuery().queryKey });
      const previous = getOnboardingProcessStepsQueryData(queryClient);

      if (previous) {
        const deletedIds = new Set(ids);
        setOnboardingProcessStepsQueryData(
          queryClient,
          previous.filter((processStep) => !deletedIds.has(processStep.id)),
        );
      }

      return { previous };
    },
    onError: (err: Error, _ids, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previous) {
        setOnboardingProcessStepsQueryData(queryClient, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(onboardingProcessStepsQuery());
      void queryClient.invalidateQueries(companyQuery());
    },
  });

  return { bulkDeleteOnboardingProcessSteps: mutate, ...rest };
};
